import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Users, Calendar, UserCog, LogOut, LayoutDashboard, ClipboardCheck, Palette, PieChart, CreditCard, History, Tags, UserCheck, MapPin, Menu, X, Cake, Bot, Share2, Timer, Dumbbell, Package, Gift, Contact, LockKeyhole, Boxes, Settings, ChevronRight, Server, DoorOpen, KeyRound } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase, rankMembers } from '@/lib';
import { usePermissions, useCurrentAdmin, useMembers, usePinSwitchDevice, useDeviceLock, useCheckInWithLocker, useInvalidateMemberProfile, useBulkMemberPhotos, useDebouncedValue } from '@/hooks';
import { AdminAvatar } from '@/components/admins';
import { SectionKey } from '@/types/permissions';
import { Member } from '@/types';
import type { MemberPhoto } from '@/types/photo';
import MemberSearchableDropdown from '@/components/ui/MemberSearchableDropdown';
import CheckInModal from '@/components/check-ins/CheckInModal';

/** How many members the global header search shows before truncating. */
const HEADER_SEARCH_RESULT_LIMIT = 8;

/**
 * Stable empty map, so the header's rows always read photos from the batched
 * fetch — passing `undefined` while it loads would send each row back to
 * fetching its own.
 */
const NO_PHOTOS: Record<string, MemberPhoto> = {};

/**
 * Routes that opt out of the 1280px (`max-w-7xl`) reading-width cap.
 *
 * That cap is a text measure — right for forms and detail pages, wrong for a
 * week grid, where the leftover width is what a chip needs to show its coach
 * and capacity instead of dropping to a bare title. Matching is exact, so the
 * class detail and form pages under `/classes/...` keep the default measure.
 *
 * `/reports` is here for the same reason: every tab is charts, wide tables and
 * multi-column stat grids, none of which is prose. It was briefly F&B-only —
 * widened per-tab, keyed off `?tab=` — but a content width that changed as you
 * clicked along the tab strip made the whole page jump, and the tabs that kept
 * the narrow measure had the same columns to fit. One width for the route is
 * both steadier to look at and one less thing to keep in sync when a tab is
 * added.
 *
 * The header row uses the same value so the search bar's left edge and the
 * admin block's right edge stay aligned with page content on every route.
 */
const WIDE_LAYOUT_ROUTES = new Set(['/classes', '/reports']);

const DEFAULT_CONTENT_WIDTH = 'max-w-7xl';
const WIDE_CONTENT_WIDTH = 'max-w-[100rem]';

// Navigation items with their permission requirements
const navigationItems = [
  // Main section (accessible to all roles)
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, section: 'DASHBOARD' as SectionKey },
  { name: 'Members', href: '/members', icon: Users, section: 'MEMBERS' as SectionKey },
  { name: 'Classes', href: '/classes', icon: Calendar, section: 'CLASSES' as SectionKey },
  { name: 'Memberships', href: '/memberships', icon: CreditCard, section: 'MEMBERSHIPS' as SectionKey },
  { name: 'Personal Training', href: '/personal-training', icon: Dumbbell, section: 'PERSONAL_TRAINING' as SectionKey },
  { name: 'Gift Cards', href: '/gift-cards', icon: Gift, section: 'GIFT_CARDS' as SectionKey },
  { name: 'Check-Ins', href: '/check-ins', icon: ClipboardCheck, section: 'CHECK_INS' as SectionKey },
  { name: 'Spaces', href: '/spaces', icon: DoorOpen, section: 'SPACES' as SectionKey },
  { name: 'Lockers', href: '/lockers', icon: KeyRound, section: 'LOCKERS' as SectionKey },
  { name: 'Birthdays', href: '/birthdays', icon: Cake, section: 'BIRTHDAYS' as SectionKey },
  { name: 'Agent', href: '/agent', icon: Bot, section: 'AGENT' as SectionKey },

  // Admin section (super_admin only)
  { name: 'Reports', href: '/reports', icon: PieChart, section: 'REPORTS' as SectionKey },
  { name: 'Admin Users', href: '/admin-users', icon: UserCog, section: 'ADMIN_USERS' as SectionKey },
  { name: 'Membership Types', href: '/membership-types', icon: CreditCard, section: 'MEMBERSHIP_TYPES' as SectionKey },
  { name: 'Class Categories', href: '/class-categories', icon: Tags, section: 'CLASS_CATEGORIES' as SectionKey },
  { name: 'Class Locations', href: '/class-locations', icon: MapPin, section: 'CLASS_LOCATIONS' as SectionKey },
  { name: 'Coaches', href: '/coaches', icon: UserCheck, section: 'COACHES' as SectionKey },
  { name: 'Employees', href: '/employees', icon: Contact, section: 'EMPLOYEES' as SectionKey },
  { name: 'Inventory', href: '/inventory', icon: Boxes, section: 'INVENTORY' as SectionKey },
  { name: 'PT Pack Types', href: '/pt-pack-types', icon: Package, section: 'PT_PACK_TYPES' as SectionKey },
  { name: 'Activity Logs', href: '/activity-logs', icon: History, section: 'ACTIVITY_LOGS' as SectionKey },
  { name: 'Style Book', href: '/style-book', icon: Palette, section: 'STYLE_BOOK' as SectionKey },
  { name: 'Odoo', href: '/odoo', icon: Share2, section: 'ODOO' as SectionKey },
  { name: 'BPA', href: '/bpa', icon: Timer, section: 'BPA' as SectionKey },
];

type NavItem = (typeof navigationItems)[number];

// Catalog/type-definition pages — rarely touched, set-and-forget config.
// Pulled out of the flat admin list into a collapsible "Configuration" group.
const CONFIG_SECTIONS: SectionKey[] = [
  'MEMBERSHIP_TYPES',
  'CLASS_CATEGORIES',
  'CLASS_LOCATIONS',
  'PT_PACK_TYPES',
];

// Integration / system / dev pages — pulled into a collapsible "System" group
// at the bottom of the admin list.
const SYSTEM_SECTIONS: SectionKey[] = [
  'ODOO',
  'BPA',
  'STYLE_BOOK',
];

function NavItemLink({
  item,
  isActive,
  indented = false,
}: {
  item: NavItem;
  isActive: boolean;
  indented?: boolean;
}) {
  const Icon = item.icon;
  return (
    <Link
      to={item.href}
      className={`
        group flex items-center py-2 text-sm font-medium rounded-md transition-colors duration-150
        ${indented ? 'pl-9 pr-2' : 'px-2'}
        ${isActive
          ? 'text-white hok-sidebar-active'
          : 'text-green-100 hover:text-white hok-sidebar-hover'
        }
      `}
    >
      <Icon className="mr-3 h-6 w-6" />
      {item.name}
    </Link>
  );
}

// Collapsible group. Opens automatically when one of its children is the active
// route so deep links / refreshes still reveal the highlighted page.
function CollapsibleNavGroup({
  name,
  icon: Icon,
  items,
  isActiveRoute,
}: {
  name: string;
  icon: typeof Settings;
  items: NavItem[];
  isActiveRoute: (path: string) => boolean;
}) {
  const hasActiveChild = items.some((item) => isActiveRoute(item.href));
  const [open, setOpen] = useState(hasActiveChild);

  useEffect(() => {
    if (hasActiveChild) setOpen(true);
  }, [hasActiveChild]);

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        className={`
          w-full group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors duration-150
          ${hasActiveChild ? 'text-white' : 'text-green-100 hover:text-white hok-sidebar-hover'}
        `}
      >
        <Icon className="mr-3 h-6 w-6" />
        <span className="flex-1 text-left">{name}</span>
        <ChevronRight className={`h-4 w-4 transition-transform duration-150 ${open ? 'rotate-90' : ''}`} />
      </button>
      {open && (
        <div className="mt-1 space-y-1">
          {items.map((item) => (
            <NavItemLink
              key={item.name}
              item={item}
              isActive={isActiveRoute(item.href)}
              indented
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Renders the admin section: flat items in order, followed by the collapsible
// "Configuration" and "System" groups at the bottom (Configuration above System).
function AdminNav({
  adminFlatItems,
  configItems,
  systemItems,
  isActiveRoute,
}: {
  adminFlatItems: NavItem[];
  configItems: NavItem[];
  systemItems: NavItem[];
  isActiveRoute: (path: string) => boolean;
}) {
  const nodes: React.ReactNode[] = adminFlatItems.map((item) => (
    <NavItemLink key={item.name} item={item} isActive={isActiveRoute(item.href)} />
  ));

  if (configItems.length > 0) {
    nodes.push(
      <CollapsibleNavGroup
        key="__config"
        name="Configuration"
        icon={Settings}
        items={configItems}
        isActiveRoute={isActiveRoute}
      />
    );
  }

  if (systemItems.length > 0) {
    nodes.push(
      <CollapsibleNavGroup
        key="__system"
        name="System"
        icon={Server}
        items={systemItems}
        isActiveRoute={isActiveRoute}
      />
    );
  }

  return <div className="space-y-1">{nodes}</div>;
}

export default function Layout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { hasAccessTo } = usePermissions();
  const { data: currentAdmin } = useCurrentAdmin();
  const { data: members } = useMembers();
  const { enabled: pinSwitchEnabled } = usePinSwitchDevice();
  const { lock: lockDeviceNow } = useDeviceLock();
  const queryClient = useQueryClient();

  const handleLockSwitch = () => {
    lockDeviceNow();
    navigate('/switch');
  };
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [checkInMember, setCheckInMember] = useState<Member | null>(null);
  const checkInFlow = useCheckInWithLocker();
  const invalidateMemberProfile = useInvalidateMemberProfile();

  // Header member search. Unlike the other member pickers, this one is fed the
  // whole roster (~750 members and growing), so it filters and ranks here and
  // hands react-select only the best few matches. Two reasons: an unranked list
  // buries the obvious hit behind whatever order the query returned, and every
  // rendered row would otherwise fetch its own photo — react-select doesn't
  // virtualise, so opening the menu meant one query per member.
  const [searchQuery, setSearchQuery] = useState('');

  const { members: rankedMembers, totalMatches } = useMemo(
    () => rankMembers(members, searchQuery, HEADER_SEARCH_RESULT_LIMIT),
    [members, searchQuery],
  );

  const headerMemberOptions = useMemo(
    () =>
      rankedMembers.map((member) => ({
        value: member.id,
        label: `${member.first_name} ${member.last_name}`,
        member,
      })),
    [rankedMembers],
  );

  // Debounced so a burst of keystrokes doesn't fire a photo query per
  // character — each distinct id set is its own React Query cache key.
  const rankedMemberIds = useMemo(() => rankedMembers.map((member) => member.id), [rankedMembers]);
  const debouncedMemberIds = useDebouncedValue(rankedMemberIds, 150);
  // keepPreviousData: each id list is its own cache key, so without it every
  // keystroke blanks the thumbnails back to placeholders while the next fetch
  // lands. Photos are looked up by member id, so stale entries can't mismatch.
  const { data: headerMemberPhotos } = useBulkMemberPhotos(debouncedMemberIds, {
    keepPreviousData: true,
  });

  // Close mobile menu when navigating
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    queryClient.clear();
    await supabase.auth.signOut();
    navigate('/login');
  };

  // Trailing slashes are equivalent routes to React Router, so normalize before
  // the exact-match lookup — otherwise '/classes/' would silently fall back.
  const normalizedPath = location.pathname.replace(/\/+$/, '') || '/';
  const contentWidth = WIDE_LAYOUT_ROUTES.has(normalizedPath)
    ? WIDE_CONTENT_WIDTH
    : DEFAULT_CONTENT_WIDTH;

  const isActiveRoute = (path: string) => {
    // For top-level routes, require exact match
    if (path === location.pathname) {
      return true;
    }

    // For nested routes, check if it's a sub-path
    // But only if the current path starts with the nav item path AND
    // the next character after the nav path is a slash
    // This prevents /membership-types from matching /members
    if (location.pathname.startsWith(path) && path !== '/') {
      const nextChar = location.pathname.charAt(path.length);
      return nextChar === '/' || nextChar === '';
    }

    return false;
  };

  // Filter navigation items based on permissions
  const allowedNavItems = navigationItems.filter(item => hasAccessTo(item.section));

  // Group navigation items by main/admin sections for display.
  // Splits are based on the order in navigationItems — main items up to and including
  // Agent, then the admin section starts at Admin Users. Because permission filtering
  // removes admin-only entries for non-super admins, split by the Agent section key.
  const agentIndex = allowedNavItems.findIndex(item => item.section === 'AGENT');
  const splitIndex = agentIndex === -1 ? allowedNavItems.length : agentIndex + 1;
  const mainItems = allowedNavItems.slice(0, splitIndex);
  const adminItems = allowedNavItems.slice(splitIndex);

  // Pull the catalog/type-definition pages into a collapsible "Configuration"
  // group and the integration/dev pages into a "System" group; the rest of the
  // admin items stay as a flat list.
  const configItems = adminItems.filter(item => CONFIG_SECTIONS.includes(item.section));
  const systemItems = adminItems.filter(item => SYSTEM_SECTIONS.includes(item.section));
  const adminFlatItems = adminItems.filter(
    item => !CONFIG_SECTIONS.includes(item.section) && !SYSTEM_SECTIONS.includes(item.section)
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 hok-sidebar-bg">
        <div className="flex items-center justify-between h-14 px-4">
          <button type="button"
            onClick={() => setMobileMenuOpen(true)}
            className="text-green-100 hover:text-white p-2 -ml-2"
            aria-label="Open menu"
          >
            <Menu className="h-6 w-6" />
          </button>
          <img src="/hok-icon.svg" alt="House of Kairos logo" className="h-8 w-auto" />
          <div className="w-10" /> {/* Spacer for centering */}
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div
          className="md:hidden fixed inset-0 z-50 bg-black/50"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Slide-Out Menu */}
      <div
        className={`md:hidden fixed inset-y-0 left-0 z-50 w-64 transform transition-transform duration-300 ease-in-out ${
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full hok-sidebar-bg">
          <div className="flex items-center justify-between pt-5 pb-4 px-4">
            <img src="/hok-icon.svg" alt="House of Kairos logo" className="h-[40px] w-auto" />
            <button type="button"
              onClick={() => setMobileMenuOpen(false)}
              className="text-green-100 hover:text-white p-2 -mr-2"
              aria-label="Close menu"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          <div className="flex-grow flex flex-col overflow-y-auto">
            <nav className="flex-1 px-2 space-y-8">
              {/* Main navigation items */}
              {mainItems.length > 0 && (
                <div className="space-y-1">
                  {mainItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = isActiveRoute(item.href);
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`
                          group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors duration-150
                          ${isActive
                            ? 'text-white hok-sidebar-active'
                            : 'text-green-100 hover:text-white hok-sidebar-hover'
                          }
                        `}
                      >
                        <Icon className="mr-3 h-6 w-6" />
                        {item.name}
                      </Link>
                    );
                  })}
                </div>
              )}
              
              {/* Admin navigation items */}
              {adminItems.length > 0 && (
                <AdminNav
                  adminFlatItems={adminFlatItems}
                  configItems={configItems}
                  systemItems={systemItems}
                  isActiveRoute={isActiveRoute}
                />
              )}
            </nav>
          </div>
          <div className="flex-shrink-0 flex flex-col gap-3 border-t hok-sidebar-border p-4">
            {pinSwitchEnabled && (
              <button type="button"
                onClick={handleLockSwitch}
                className="flex items-center text-green-100 hover:text-white transition-colors duration-150 ease-in-out w-full text-sm"
              >
                <LockKeyhole className="h-5 w-5 mr-3" />
                Lock / Switch user
              </button>
            )}
            <button type="button"
              onClick={handleLogout}
              className="flex items-center text-green-100 hover:text-white transition-colors duration-150 ease-in-out w-full text-sm"
            >
              <LogOut className="h-5 w-5 mr-3" />
              Sign Out
            </button>
          </div>
        </div>
      </div>

      <div className="flex h-screen">
        {/* Desktop Sidebar */}
        <div className="hidden md:flex md:flex-shrink-0">
          <div className="flex flex-col w-64 h-full">
            <div className="flex flex-col flex-grow min-h-0 pt-5 pb-4 overflow-y-auto hok-sidebar-bg">
              <div className="flex items-center flex-shrink-0 px-4">
                <img src="/hok-icon.svg" alt="House of Kairos logo" className="h-[40px] w-auto" />
              </div>
              <div className="mt-5 flex-grow flex flex-col">
                <nav className="flex-1 px-2 space-y-8">
                  {/* Main navigation items */}
                  {mainItems.length > 0 && (
                    <div className="space-y-1">
                      {mainItems.map((item) => {
                        const Icon = item.icon;
                        const isActive = isActiveRoute(item.href);
                        return (
                          <Link
                            key={item.name}
                            to={item.href}
                            className={`
                              group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors duration-150
                              ${isActive
                                ? 'text-white hok-sidebar-active'
                                : 'text-green-100 hover:text-white hok-sidebar-hover'
                              }
                            `}
                          >
                            <Icon className="mr-3 h-6 w-6" />
                            {item.name}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                  
                  {/* Admin navigation items */}
                  {adminItems.length > 0 && (
                    <AdminNav
                      adminFlatItems={adminFlatItems}
                      configItems={configItems}
                      systemItems={systemItems}
                      isActiveRoute={isActiveRoute}
                    />
                  )}
                </nav>
              </div>
            </div>
          </div>
        </div>

        {/* Main content */}
        <div className="flex flex-col w-0 flex-1 overflow-hidden">
          {/* Desktop header: global member search + current admin + Sign Out.
              Inner row uses the same route-specific `contentWidth` wrapper as
              <main> so the search bar's left edge and admin info's right edge
              line up with page content — including on the wide routes. */}
          <header className="hidden md:flex flex-shrink-0 h-16 border-b hok-sidebar-border hok-sidebar-bg">
            <div className={`w-full ${contentWidth} mx-auto flex items-center justify-between gap-4 px-4 sm:px-6 md:px-8`}>
            <div className="w-80 header-member-search">
              <MemberSearchableDropdown
                options={headerMemberOptions}
                value={undefined}
                onChange={(memberId) => {
                  const member = memberId ? members?.find((m) => m.id === memberId) : undefined;
                  if (member) setCheckInMember(member);
                }}
                onInputChange={setSearchQuery}
                placeholder="Search name, email or phone..."
                isClearable
                optionSize="large"
                photos={headerMemberPhotos ?? NO_PHOTOS}
                maxMenuHeight={420}
                noOptionsMessage={({ inputValue }) =>
                  inputValue.trim()
                    ? 'No members match'
                    : 'Type a name, email or phone number'
                }
                menuFooter={
                  totalMatches > headerMemberOptions.length ? (
                    <div className="px-4 py-2 text-xs text-gray-500 border-t border-gray-100">
                      Showing {headerMemberOptions.length} of {totalMatches} matches — keep typing to
                      narrow
                    </div>
                  ) : null
                }
              />
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right leading-tight">
                <div className="text-sm font-medium text-white">
                  {currentAdmin?.full_name || currentAdmin?.email || ''}
                </div>
                {currentAdmin?.full_name && (
                  <div className="text-xs text-green-200">{currentAdmin.email}</div>
                )}
              </div>
              <AdminAvatar
                name={currentAdmin?.full_name}
                email={currentAdmin?.email}
                photoUrl={currentAdmin?.profile_photo_url}
                size={36}
              />
              {pinSwitchEnabled && (
                <button type="button"
                  onClick={handleLockSwitch}
                  className="inline-flex items-center gap-2 rounded-md border border-white/20 bg-transparent px-3 py-2 text-sm font-medium text-green-100 hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-green-300 focus:ring-offset-2 focus:ring-offset-[#132C1F]"
                >
                  <LockKeyhole className="h-4 w-4" />
                  Lock / Switch user
                </button>
              )}
              <button type="button"
                onClick={handleLogout}
                className="inline-flex items-center gap-2 rounded-md border border-white/20 bg-transparent px-3 py-2 text-sm font-medium text-green-100 hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-green-300 focus:ring-offset-2 focus:ring-offset-[#132C1F]"
              >
                <LogOut className="h-4 w-4" />
                Sign Out
              </button>
            </div>
            </div>
          </header>

          <main className="flex-1 relative overflow-y-auto focus:outline-none">
            <div className="mt-14 md:mt-0 pb-6">
              <div className={`${contentWidth} mx-auto px-4 sm:px-6 md:px-8`}>
                {children}
              </div>
            </div>
          </main>
        </div>
      </div>

      {/* Quick check-in from the header search — works on any page. Conditionally
          mounted so each selected member gets a fresh modal (state + refetches). */}
      {checkInMember && (
        <CheckInModal
          isOpen
          onClose={() => setCheckInMember(null)}
          preSelectedMemberId={checkInMember.id}
          preSelectedMember={checkInMember}
          linkNameToProfile
          isLoading={checkInFlow.isPending}
          onCheckIn={(_memberId, lockerId) =>
            checkInFlow.submit({
              member: checkInMember,
              lockerId,
              onSuccess: () => {
                setCheckInMember(null);
                // The admin may be looking at this member's profile right now —
                // member-scoped queries (e.g. the Check-ins tab) aren't covered
                // by useCreateCheckIn's own invalidations.
                invalidateMemberProfile(checkInMember.id);
              },
            })
          }
        />
      )}
    </div>
  );
}
